import './OnboardingView.css'

import { useState, useEffect } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { useNavigate } from 'react-router'

import { session, onboarding, websocket } from '../store'
import { useRheyaVoice } from '../hooks/useRheyaVoice'
import { playMessageVoice, unlockAudioPlayback } from '../lib/voice'
import {
  shouldLeaveOnboarding,
  shouldShowOnboardingCompletePanel,
} from '../features/rheya/onboardingLeave'
import { rheyaStatusLabel } from '../features/rheya/messageList'

import { Button } from '../components/Button'
import { Chat } from '../components/Chat'
import { ChatBubble } from '../components/ChatBubble'
import { ToolBubble } from '../components/ToolBubble'
import { useRequireAuthentication } from '../hooks/useRequireAuthentication'

export function OnboardingView() {
  useRequireAuthentication()

  const authStatus = useAtomValue(session.sessionStatusAtom)
  const currentSession = useAtomValue(session.sessionAtom)
  const connectionStatus = useAtomValue(websocket.connectionStatusAtom)
  const navigate = useNavigate()
  const [hasSeenPitch, setHasSeenPitch] = useState(false)
  const [inputMessage, setInputMessage] = useState('')

  const messages = useAtomValue(onboarding.onboardingMessagesAtom)
  const isLoading = useAtomValue(onboarding.onboardingIsLoadingAtom)
  const isComplete = useAtomValue(onboarding.onboardingIsCompleteAtom)
  const isRejected = useAtomValue(onboarding.onboardingIsRejectedAtom)
  const error = useAtomValue(onboarding.onboardingErrorAtom)
  const status = useAtomValue(onboarding.onboardingStatusAtom)

  const connectOnboarding = useSetAtom(onboarding.onboardingConnectAtom)
  const sendMessage = useSetAtom(onboarding.onboardingSendMessageAtom)
  const disconnect = useSetAtom(onboarding.onboardingDisconnectAtom)
  const reset = useSetAtom(onboarding.onboardingResetAtom)
  const playRemote = useSetAtom(onboarding.onboardingPlayMessageAtom)
  const setRemoteVoice = useSetAtom(onboarding.onboardingSetVoiceAtom)
  const voiceStopRemote = useSetAtom(onboarding.onboardingVoiceStopAtom)
  const streamHeldIds = useAtomValue(onboarding.rheyaVoiceHeldMessageIdsAtom)
  const logout = useSetAtom(session.logoutAtom)
  const updateSession = useSetAtom(session.updateSessionAtom)

  const voice = useRheyaVoice()
  const heldKeys = new Set([...voice.heldKeys, ...streamHeldIds])

  useEffect(() => {
    if (currentSession?.user.profile) {
      navigate('/profile', { replace: true })
    }
  }, [currentSession, navigate])

  useEffect(() => {
    return () => {
      voiceStopRemote()
      void voice.stop()
      disconnect()
      reset()
    }
  }, [disconnect, reset, voiceStopRemote])

  // Wait for farewell TTS before redirect (matches mobile)
  useEffect(() => {
    if (!shouldLeaveOnboarding({ isComplete, playbackPending: voice.playbackPending })) return

    let cancelled = false
    const settleMs = isRejected ? 1200 : 900
    const timer = setTimeout(() => {
      void (async () => {
        if (cancelled) return
        if (isRejected) {
          localStorage.removeItem('authToken')
          await logout()
          navigate('/login', { replace: true })
          return
        }
        await updateSession()
        navigate('/profile', { replace: true })
      })()
    }, settleMs)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [isComplete, isRejected, voice.playbackPending, logout, navigate, updateSession])

  useEffect(() => {
    if (hasSeenPitch && connectionStatus === 'connected') {
      connectOnboarding()
    }
  }, [hasSeenPitch, connectionStatus, connectOnboarding])

  const setHeldIds = useSetAtom(onboarding.rheyaVoiceHeldMessageIdsAtom)

  // Keep server live-TTS flag in sync with mute; unhold bubbles when muted.
  useEffect(() => {
    if (!hasSeenPitch || connectionStatus !== 'connected') return
    setRemoteVoice(!voice.muted)
    if (voice.muted) setHeldIds(new Set())
  }, [voice.muted, hasSeenPitch, connectionStatus, setRemoteVoice, setHeldIds])

  const handleVideoComplete = () => {
    unlockAudioPlayback()
    setHasSeenPitch(true)
  }

  const deliverVoiceText = (text: string) => {
    const body = text.trim()
    if (!body || isLoading || isComplete) return
    sendMessage(body)
  }

  useEffect(() => {
    voice.setOnAutoSend(deliverVoiceText)
    return () => voice.setOnAutoSend(null)
  }, [isLoading, isComplete])

  const handleSubmit = () => {
    if (inputMessage.trim() && !isComplete) {
      sendMessage(inputMessage.trim())
      setInputMessage('')
    }
  }

  if (authStatus !== 'authenticated') {
    return <div className="onboarding-view onboarding-view--loading">Loading...</div>
  }

  if (!hasSeenPitch) {
    return (
      <div className="onboarding-view onboarding-view--pitch">
        <div className="onboarding-view__pitch-content">
          <h1 className="onboarding-view__title">Welcome to Revel Club</h1>
          <p className="onboarding-view__description">
            Watch this short video to learn about our platform and community guidelines before your
            matchmaker interview.
          </p>
          <div className="onboarding-view__video">Video player placeholder</div>
          <Button kind="primary" onClick={handleVideoComplete}>
            Continue to Interview
          </Button>
        </div>
      </div>
    )
  }

  if (shouldShowOnboardingCompletePanel({ isComplete, playbackPending: voice.playbackPending })) {
    return (
      <div className="onboarding-view onboarding-view--complete">
        <div className="onboarding-view__complete-content">
          {isRejected ? (
            <>
              <div className="onboarding-view__complete-icon onboarding-view__complete-icon--rejected" />
              <h1 className="onboarding-view__title">Thank You for Applying</h1>
              <p className="onboarding-view__description">
                We appreciate your interest in Revel Club. Unfortunately, we&apos;re unable to offer
                membership at this time. You&apos;re welcome to reapply in the future.
              </p>
            </>
          ) : (
            <>
              <div className="onboarding-view__complete-icon onboarding-view__complete-icon--success" />
              <h1 className="onboarding-view__title">You&apos;re All Set!</h1>
              <p className="onboarding-view__description">
                We&apos;ve learned enough about you to find great matches. Redirecting you now...
              </p>
            </>
          )}
        </div>
      </div>
    )
  }

  const softStatus = rheyaStatusLabel(status, heldKeys.size)

  return (
    <div className="onboarding-view onboarding-view--chat">
      <header className="onboarding-view__header">
        <div>
          <h2 className="onboarding-view__title">Matchmaker Interview</h2>
          <p className="onboarding-view__description">
            Chat with Rheya, the matchmaker for Revel Club, so we can understand who you are and
            what you&apos;re looking for.
          </p>
        </div>
        <button
          type="button"
          className="onboarding-view__mute"
          onClick={voice.toggleMute}
          aria-label={voice.muted ? 'Unmute Rheya' : 'Mute Rheya'}
        >
          {voice.muted ? 'Muted' : voice.speaking ? 'Speaking…' : 'Voice on'}
        </button>
      </header>

      {(error || voice.voiceError) && (
        <div className="onboarding-view__error">{error || voice.voiceError}</div>
      )}

      <Chat
        className="onboarding-view__chat"
        messages={messages}
        inputValue={inputMessage}
        inputPlaceholder={isComplete ? 'Session complete' : 'Type your response...'}
        disabled={isLoading || isComplete}
        heldKeys={heldKeys}
        playingKey={voice.playingKey}
        onPlayMessage={(text, key) => {
          void playMessageVoice(key, () => playRemote(key), text).catch((err: unknown) => {
            console.warn('[Onboarding] play failed', err)
          })
        }}
        indicator={
          status.type === 'toolUse' ? (
            <ToolBubble toolUse={status.toolUse} active />
          ) : softStatus ? (
            <ChatBubble sender="status">{softStatus}</ChatBubble>
          ) : null
        }
        onInputChange={setInputMessage}
        onSubmit={handleSubmit}
        showMic={!isComplete}
        recording={voice.recording}
        micBusy={voice.voiceBusy}
        levels={voice.levels}
        onMicPress={() => {
          void voice.startRecording()
        }}
        onCancelRecording={() => {
          void voice.cancelRecording()
        }}
        onSendRecording={() => {
          void (async () => {
            const text = await voice.sendRecording()
            if (text) deliverVoiceText(text)
          })()
        }}
      />
    </div>
  )
}
